import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { FirebaseAppContext } from 'libs.firebase_react';
import { getLanguageAndRegionDisplayNames, Locale } from 'libs.i18n-core';
import { LocalizationStatus } from 'libs.localization.common';
import { useLocalizeMessage, Table, Badge } from 'libs.nucleus';
const statusMap = {
    [LocalizationStatus.READY]: {
        color: 'success',
        label: 'Ready',
    },
    [LocalizationStatus.IN_PROGRESS]: {
        color: 'incomplete',
        label: 'In progress',
    },
    [LocalizationStatus.IN_REVIEW]: {
        color: 'incomplete',
        label: 'In review',
    },
    [LocalizationStatus.NOT_STARTED]: {
        color: 'incomplete',
        label: 'Not Ready',
    },
};
export const LocalesSelection = ({ onLocaleSelection, locales, isLocaleReady }) => {
    const { studyId } = useParams();
    const t = useLocalizeMessage();
    const { logEvent } = useContext(FirebaseAppContext);
    const columns = [
        {
            header: t('Code'),
            accessorKey: 'locale',
            cell: (value) => {
                return value.getValue();
            },
        },
        {
            header: t('Language/script'),
            accessorKey: 'language',
        },
        {
            header: t('Country/region'),
            accessorKey: 'region',
        },
        {
            header: t('Status'),
            accessorKey: 'status',
            cell: (value) => {
                const status = value.getValue();
                const currentStatus = statusMap[status];
                const variant = currentStatus?.color || 'incomplete';
                return _jsx(Badge, { variant: variant, label: t(currentStatus.label) });
            },
        },
    ];
    const data = locales.map((locale) => {
        const { languageDisplayName, regionDisplayName } = getLanguageAndRegionDisplayNames({
            appLocale: Locale.en_US,
            targetLocaleCode: locale,
        });
        const status = isLocaleReady(locale) ? LocalizationStatus.READY : LocalizationStatus.NOT_STARTED;
        return {
            locale,
            language: languageDisplayName,
            region: regionDisplayName,
            status,
        };
    });
    const handleOnSearch = useCallback((searchValue) => {
        logEvent('submission_package_modal_search', {
            studyId,
            searchValue,
        });
    }, []);
    const onRowSelectionChange = useCallback((selectedRows) => {
        const selectedLocales = selectedRows
            .map((row) => locales[typeof row === 'string' ? Number(row) : row])
            .filter((locale) => Boolean(locale));
        onLocaleSelection(selectedLocales);
    }, [locales]);
    return (_jsx("div", { className: 'flex flex-col gap-5', children: _jsx(Table, { onSearch: handleOnSearch, data: data, columns: columns, labelNoResults: t('No Locales'), useInternalSearch: true, hasPagination: true, initialPageSize: 10, labelSearchField: t('Search'), isSelectable: true, enableRowSelection: (row) => row.original.status === LocalizationStatus.READY, onRowSelectionChange: onRowSelectionChange, paginationNavigationScreenReaderLabel: t('Locale table navigation') }) }));
};
